import { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Button, Input } from "@legal-500/legal500-design-system";
import type {
  CreateMailingGroupBody,
  MailingGroupDetailRegion,
  UpdateMailingGroupBody,
} from "@/api/mailing-groups.service";
import { BAR_GROUP_LABELS, publicationDisplayLabel } from "./mailingGroups.utils";
import { regionKey, useRankingEntityPicker, useRegionPicker } from "./useRegionPicker";
import { useRefereesPreview } from "./useRefereesPreview";
import { RegionPickerSection } from "./RegionPickerSection";
import styles from "./MailingGroups.module.css";

type EditGroup = {
  id: string | number;
  name: string;
  regions: (Pick<MailingGroupDetailRegion, "region_id" | "region_name"> &
    Partial<Pick<MailingGroupDetailRegion, "ranking_entity_id">>)[];
};

type AddGroupModalProps = {
  isOpen: boolean;
  onClose: () => void;
  publicationId: number | null;
  editionId?: string;
  publicationName: string;
  publicationEdition?: string;
  edition: string;
  onSave: (body: CreateMailingGroupBody) => Promise<void>;
  editGroup?: EditGroup;
  onUpdate?: (id: string | number, body: UpdateMailingGroupBody) => Promise<void>;
};

const ADD_INTRO_TEXT =
  "Choose which regions to include in the new group and give it a name. Regions already " +
  "assigned to a group in this publication are disabled, so the same region can't be used twice.";

const EDIT_INTRO_TEXT =
  "Update the regions and name for this group. The group's own regions stay selectable.";

function AddGroupModal({
  isOpen,
  onClose,
  publicationId,
  editionId,
  publicationName,
  publicationEdition,
  edition,
  onSave,
  editGroup,
  onUpdate,
}: AddGroupModalProps) {
  const isEdit = !!editGroup;
  const newAxis = editionId !== undefined;

  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const wasOpenRef = useRef(false);

  const excludeUsedRegionIds = useMemo(
    () =>
      editGroup && !newAxis
        ? editGroup.regions.flatMap(r => (r.region_id === null ? [] : [r.region_id]))
        : undefined,
    [editGroup, newAxis],
  );
  const excludeUsedRankingEntityIds = useMemo(
    () =>
      editGroup && newAxis
        ? editGroup.regions.flatMap(r => (r.ranking_entity_id ? [r.ranking_entity_id] : []))
        : undefined,
    [editGroup, newAxis],
  );
  const initialSelectedRegionKeys = useMemo(
    () =>
      editGroup
        ? editGroup.regions.map(r =>
            regionKey({
              id: newAxis ? (r.ranking_entity_id ?? null) : r.region_id,
              name: r.region_name,
              type: null,
            }),
          )
        : undefined,
    [editGroup, newAxis],
  );

  const legacyPicker = useRegionPicker({
    publicationId,
    edition,
    enabled: isOpen && !newAxis,
    excludeUsedRegionIds,
    initialSelectedRegionKeys: newAxis ? undefined : initialSelectedRegionKeys,
  });

  const rankingEntityPicker = useRankingEntityPicker({
    editionId: editionId ?? null,
    enabled: isOpen && newAxis,
    excludeUsedRegionIds: excludeUsedRankingEntityIds,
    initialSelectedRegionKeys: newAxis ? initialSelectedRegionKeys : undefined,
  });

  const picker = newAxis ? rankingEntityPicker : legacyPicker;

  const selectedRegionIds = useMemo(
    () =>
      legacyPicker.selectedRegions
        .map(r => r.id as number)
        .filter((id): id is number => typeof id === "number"),
    [legacyPicker.selectedRegions],
  );

  const selectedRankingEntityIds = useMemo(
    () => rankingEntityPicker.selectedRegions.map(entity => entity.id),
    [rankingEntityPicker.selectedRegions],
  );

  const {
    previewLoading,
    previewError,
    uniqueEmails: uniqueEmailCount,
  } = useRefereesPreview(
    editionId !== undefined
      ? {
          editionId,
          rankingEntityIds: selectedRankingEntityIds,
          enabled: !picker.isMixed,
        }
      : {
          publicationId,
          regionIds: selectedRegionIds,
          enabled: !picker.isMixed,
        },
  );

  // Reset local state when the modal (re)opens.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setSubmitting(false);
      setErrors({});
      if (editGroup) {
        // Edit mode: pre-fill the name; the picker pre-selects regions via
        // initialSelectedRegionKeys, so don't reset() (it would clear them).
        setName(editGroup.name);
      } else {
        setName("");
        picker.reset();
      }
    }
    wasOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const clearError = (field: string) => {
    setErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleToggleRegion = (region: Parameters<typeof picker.toggleRegion>[0]) => {
    picker.toggleRegion(region);
    clearError("regions");
    clearError("name");
  };

  const handleSelectAll = () => {
    picker.selectAllRegions();
    clearError("regions");
  };

  const handleSelectNonBar = () => {
    picker.selectNonBarRegions();
    clearError("regions");
  };

  const handleSelectBar = () => {
    picker.selectBarRegions();
    clearError("regions");
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (picker.selectedRegions.length === 0) next.regions = "Select at least one region";
    if (!name.trim()) {
      next.name = "Group name is required";
    }
    if (picker.selectionKind === "mixed") {
      next.regions = "Bar and standard regions cannot be mixed in one group";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const rankingEntityRegions = rankingEntityPicker.selectedRegions.map(entity => ({
      rankingEntityId: entity.id,
      regionName: entity.name,
    }));
    const legacyRegions = legacyPicker.selectedRegions.map(region => ({
      regionId: region.id as number,
      regionName: region.name ?? undefined,
    }));
    const fields = {
      publicationName: publicationName || undefined,
      publicationEdition: publicationEdition || undefined,
      edition,
      name: name.trim(),
    };

    // On success the parent closes + refetches. If the call throws, keep the
    // modal open so the user can correct; let the error propagate.
    try {
      setSubmitting(true);

      if (isEdit && editGroup && onUpdate) {
        const updateBody: UpdateMailingGroupBody = {
          name: name.trim(),
          regions: newAxis ? rankingEntityRegions : legacyRegions,
        };
        await onUpdate(editGroup.id, updateBody);
      } else if (editionId !== undefined) {
        await onSave({ editionId, ...fields, regions: rankingEntityRegions });
      } else if (publicationId !== null) {
        await onSave({ publicationId, ...fields, regions: legacyRegions });
      }

      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const canEditRegions = picker.regions.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit group" : "Add group"}
      size="mediumLarge"
      backdropClick={false}
    >
      <div className={styles.modalForm}>
        <p className={styles.modalIntro}>{isEdit ? EDIT_INTRO_TEXT : ADD_INTRO_TEXT}</p>

        <div className={styles.readonlyField}>
          <span className={styles.readonlyLabel}>Publication</span>
          <span className={styles.readonlyValue}>
            {edition} - {publicationDisplayLabel(publicationName, publicationEdition)}
          </span>
        </div>

        <RegionPickerSection
          regions={picker.regions}
          selectedRegionKeys={picker.selectedRegionKeys}
          usedRegionsById={picker.usedRegionsById}
          isRegionUsed={picker.isRegionUsed}
          isRegionSelectable={picker.isRegionSelectable}
          isBar={picker.isBar}
          onToggle={handleToggleRegion}
          onSelectAll={handleSelectAll}
          onClear={picker.clearRegions}
          showActions={canEditRegions}
          onSelectNonBar={handleSelectNonBar}
          onSelectBar={handleSelectBar}
          countriesLoading={picker.countriesLoading}
          countriesError={picker.countriesError}
          usedRegionLabelMode="groupName"
          error={errors.regions}
        />

        {picker.isMixed && (
          <div className={styles.warningBanner} role="alert">
            Bar and standard regions cannot be mixed in a single mailing group. Deselect one type to
            continue.
          </div>
        )}

        {!picker.isMixed && picker.isAllBar && picker.selectedRegions.length > 0 && (
          <div className={styles.infoBanner}>
            Bar regions produce 2 mailing groups each ({BAR_GROUP_LABELS.join(" / ")}).
          </div>
        )}

        <div className={styles.formFieldFull}>
          <Input
            label="Group name"
            value={name}
            onChange={e => {
              setName(e.target.value);
              clearError("name");
            }}
            fullWidth
            placeholder="e.g. DACH, Nordics, Benelux…"
            helperText="Give the group a recognisable name."
            required
            error={errors.name}
          />
        </div>

        <div className={styles.countSummaryRow}>
          <div className={styles.countSummary}>
            <span className={styles.countLabel}>Mailing groups to be generated</span>
            <span className={styles.countValue} data-testid="group-count">
              {picker.groupCount}
            </span>
          </div>
          <div className={styles.countSummary}>
            <span className={styles.countLabel}>Unique emails in selection</span>
            <span className={styles.countValue} data-testid="unique-email-count">
              {uniqueEmailCount}
            </span>
          </div>
        </div>

        {/* Always rendered so idle/loading/error keep the same height — a
            conditionally mounted line re-centers the modal ("jump"). */}
        <p
          className={previewError ? styles.previewHintError : styles.previewHint}
          aria-live="polite"
        >
          {previewLoading ? "Loading preview…" : previewError ? previewError : " "}
        </p>

        <div className={styles.modalFooter}>
          <Button variant="outlined" size="small" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSave}
            disabled={submitting || picker.isMixed || picker.groupCount === 0}
          >
            {isEdit
              ? submitting
                ? "Saving…"
                : "Save changes"
              : submitting
                ? "Adding…"
                : "Add group"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export { AddGroupModal };
